import React, { useEffect } from 'react'
import { getUserInfoSync } from '../../config/api'

const SideBar = ({ activeScreen, setActiveScreen, isFirstLogin }) => {
  const userInfo = getUserInfoSync()
  const firstLogin = isFirstLogin || userInfo?.first_login === true

  const handleMenuClick = (screen) => {
    // If first login, only allow profile screen
    if (firstLogin && screen !== 'profile') {
      return
    }
    setActiveScreen(screen)
  }

  const isActive = (screenOrScreens) => {
    if (Array.isArray(screenOrScreens)) {
      return screenOrScreens.includes(activeScreen) ? '' : 'collapsed'
    }
    return activeScreen === screenOrScreens ? '' : 'collapsed'
  }

  const isSubActive = (screen) => {
    return activeScreen === screen ? 'active' : ''
  }

  const isExpanded = (screens) => {
    return screens.includes(activeScreen) ? 'collapse show' : 'collapse'
  }

  // Ensure parent menus are expanded when a sub-item is active
  useEffect(() => {
    const menuMappings = {
      'products': '#products-nav',
      'add-products': '#products-nav',
      'import-products': '#products-nav',
      'inventory-overview': '#products-nav',
      'stock-history': '#products-nav',
      'transfer-warehouses': '#transfer-nav',
      'transfer-branches': '#transfer-nav',
      'stock-transfers': '#transfer-nav',
      'suppliers': '#purchases-nav',
      'purchase-orders': '#purchases-nav',
      'purchase-reports': '#purchases-nav',
      'sales': '#sales-nav',
      'orders': '#sales-nav',
      'quotations': '#sales-nav',
      'system-admin': '#staff-nav',
      'users': '#staff-nav',
      'audit-trail': '#staff-nav'
    }

    const targetId = menuMappings[activeScreen]
    if (targetId) {
      const collapseElement = document.querySelector(targetId)
      if (collapseElement && window.bootstrap && window.bootstrap.Collapse) {
        let bsCollapse = window.bootstrap.Collapse.getInstance(collapseElement)
        if (!bsCollapse) {
          bsCollapse = new window.bootstrap.Collapse(collapseElement, { toggle: false })
        }
        if (!collapseElement.classList.contains('show')) {
          bsCollapse.show()
        }
      }
    }
  }, [activeScreen])

  return (
    <aside id="sidebar" className="sidebar">
      <ul className="sidebar-nav" id="sidebar-nav">
        {/* Dashboard */}
        <li className="nav-item">
          <a 
            className={`nav-link ${isActive('dashboard')} ${firstLogin ? 'disabled' : ''}`} 
            href="#"
            onClick={(e) => { e.preventDefault(); handleMenuClick('dashboard'); }}
            style={firstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            <i className="bi bi-speedometer2" />
            <span>Dashboard</span>
          </a>
        </li>

        {/* Products & Inventory */}
        <li className="nav-item">
          <a 
            className={`nav-link ${isActive(['products', 'add-products', 'import-products', 'inventory-overview', 'stock-history'])} ${firstLogin ? 'disabled' : ''}`} 
            data-bs-target="#products-nav" 
            data-bs-toggle={firstLogin ? '' : 'collapse'} 
            href="#"
            onClick={(e) => { e.preventDefault(); }}
            style={firstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            <i className="bi bi-box-seam" />
            <span>Inventory</span>
            <i className="bi bi-chevron-down ms-auto" />
          </a>
          <ul id="products-nav" className={`nav-content ${isExpanded(['products', 'add-products', 'import-products', 'inventory-overview', 'stock-history'])}`} data-bs-parent="#sidebar-nav">
            <li>
              <a 
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('products'); }}
                className={isSubActive('products')}
                style={firstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
              >
                <i className="bi bi-circle" />
                <span>Products</span>
              </a>
            </li>
            <li>
              <a 
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('add-products'); }}
                className={isSubActive('add-products')}
                style={firstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
              >
                <i className="bi bi-circle" />
                <span>Add Products</span>
              </a>
            </li>
            <li>
              <a 
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('import-products'); }}
                className={isSubActive('import-products')}
                style={firstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
              >
                <i className="bi bi-circle" />
                <span>Import Products</span>
              </a>
            </li>
            <li>
              <a 
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('inventory-overview'); }}
                className={isSubActive('inventory-overview')}
                style={firstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
              >
                <i className="bi bi-circle" />
                <span>Inventory Overview</span>
              </a>
            </li>
            <li>
              <a 
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('stock-history'); }}
                className={isSubActive('stock-history')}
                style={firstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
              >
                <i className="bi bi-circle" />
                <span>Stock History</span>
              </a>
            </li>
          </ul>
        </li>       

        {/* Transfer Warehouses & Branches */}
        <li className="nav-item">
          <a 
            className={`nav-link ${isActive(['branches', 'transfer-warehouses', 'transfer-branches', 'stock-transfers'])} ${firstLogin ? 'disabled' : ''}`} 
            data-bs-target="#transfer-nav" 
            data-bs-toggle={firstLogin ? '' : 'collapse'} 
            href="#"
            onClick={(e) => { e.preventDefault(); }}
            style={firstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            <i className="bi bi-arrow-left-right" />
            <span>Branches</span>
            <i className="bi bi-chevron-down ms-auto" />
          </a>
          <ul id="transfer-nav" className={`nav-content ${isExpanded(['branches', 'transfer-warehouses', 'transfer-branches', 'stock-transfers'])}`} data-bs-parent="#sidebar-nav">
            <li>
              <a 
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('transfer-warehouses'); }}
                className={isSubActive('transfer-warehouses')}
                style={firstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
              >
                <i className="bi bi-circle" />
                <span>Warehouses</span>
              </a>
            </li>
            <li>
              <a 
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('branches'); }}
                className={isSubActive('branches')}
                style={firstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
              >
                <i className="bi bi-circle" />
                <span>Branches</span>
              </a>
            </li>
            <li>
              <a 
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('transfer-branches'); }}
                className={isSubActive('transfer-branches')}
                style={firstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
              >
                <i className="bi bi-circle" />
                <span>Branch Transfers</span>
              </a>
            </li>
            <li>
              <a 
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('stock-transfers'); }}
                className={isSubActive('stock-transfers')}
                style={firstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
              >
                <i className="bi bi-circle" />
                <span>Stock Transfers</span>
              </a>
            </li>
          </ul>
        </li>

        {/* Purchases & Suppliers */}
        <li className="nav-item">
          <a 
            className={`nav-link ${isActive(['suppliers', 'purchase-orders', 'purchase-reports'])} ${firstLogin ? 'disabled' : ''}`} 
            data-bs-target="#purchases-nav" 
            data-bs-toggle={firstLogin ? '' : 'collapse'} 
            href="#"
            onClick={(e) => { e.preventDefault(); }}
            style={firstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            <i className="bi bi-cart-check" />
            <span>Purchases & Suppliers</span>
            <i className="bi bi-chevron-down ms-auto" />
          </a>
          <ul id="purchases-nav" className={`nav-content ${isExpanded(['suppliers', 'purchase-orders', 'purchase-reports'])}`} data-bs-parent="#sidebar-nav">
            <li>
              <a 
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('suppliers'); }}
                className={isSubActive('suppliers')}
                style={firstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
              >
                <i className="bi bi-circle" />
                <span>Suppliers</span>
              </a>
            </li>
          
           
          </ul>
        </li>

        {/* Sales */}
        <li className="nav-item">
          <a 
            className={`nav-link ${isActive(['sales', 'orders', 'quotations'])} ${firstLogin ? 'disabled' : ''}`} 
            data-bs-target="#sales-nav" 
            data-bs-toggle={firstLogin ? '' : 'collapse'} 
            href="#"
            onClick={(e) => { e.preventDefault(); }}
            style={firstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            <i className="bi bi-cash-stack" />
            <span>Sales</span>
            <i className="bi bi-chevron-down ms-auto" />
          </a>
          <ul id="sales-nav" className={`nav-content ${isExpanded(['sales', 'orders', 'quotations'])}`} data-bs-parent="#sidebar-nav">
            <li>
              <a 
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('sales'); }}
                className={isSubActive('sales')}
                style={firstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
              >
                <i className="bi bi-circle" />
                <span>Sales</span>
              </a>
            </li>
           
           
          </ul>
        </li>

        {/* Staff & Roles */}
        <li className="nav-item">
          <a 
            className={`nav-link ${isActive(['system-admin', 'users', 'audit-trail'])} ${firstLogin ? 'disabled' : ''}`} 
            data-bs-target="#staff-nav" 
            data-bs-toggle={firstLogin ? '' : 'collapse'} 
            href="#"
            onClick={(e) => { e.preventDefault(); }}
            style={firstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            <i className="bi bi-people-fill" />
            <span>Staff & Roles</span>
            <i className="bi bi-chevron-down ms-auto" />
          </a>
          <ul id="staff-nav" className={`nav-content ${isExpanded(['system-admin', 'users', 'audit-trail'])}`} data-bs-parent="#sidebar-nav">
            <li>
              <a 
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('system-admin'); }}
                className={isSubActive('system-admin')}
                style={firstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
              >
                <i className="bi bi-circle" />
                <span>Create System Admin</span>
              </a>
            </li>
            <li>
              <a 
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('users'); }}
                className={isSubActive('users')}
                style={firstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
              >
                <i className="bi bi-circle" />
                <span>Users</span>
              </a>
            </li>
           
          </ul>
        </li>

        {/* Reports */}
        <li className="nav-item">
          <a 
            className={`nav-link ${isActive('reports')} ${firstLogin ? 'disabled' : ''}`} 
            href="#"
            onClick={(e) => { e.preventDefault(); handleMenuClick('reports'); }}
            style={firstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            <i className="bi bi-file-earmark-text" />
            <span>Reports</span>
          </a>
        </li>

        {/* Profile */}
        <li className="nav-item">
          <a 
            className={`nav-link ${isActive('profile')}`} 
            href="#"
            onClick={(e) => { e.preventDefault(); handleMenuClick('profile'); }}
          >
            <i className="bi bi-person" />
            <span>Profile</span>
          </a>
        </li>
      </ul>
    </aside>
  )
}

export default SideBar